import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Upload, Mail, User } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import AdminLayout from './AdminLayout';

const API_BASE_URL = 'http://localhost:5000';

const GerenciarInstrutores = () => {
  const [instrutores, setInstrutores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedInstrutor, setSelectedInstrutor] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    bio: '',
    especialidades: '',
    foto: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    carregarInstrutores();
  }, []);

  const carregarInstrutores = async () => {
    try {
      setLoading(true);
      const response = await api.get('/instrutores');
      setInstrutores(response.data.instrutores || []);
    } catch (error) {
      console.error('Erro ao carregar instrutores:', error);
      alert('Erro ao carregar instrutores');
    } finally {
      setLoading(false);
    }
  };

  const filteredInstrutores = instrutores.filter(instrutor =>
    instrutor.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instrutor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instrutor.especialidades?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const abrirModal = (mode, instrutor = null) => {
    setModalMode(mode);
    setSelectedInstrutor(instrutor);
    
    if (mode === 'edit' && instrutor) {
      setFormData({
        nome: instrutor.nome || '',
        email: instrutor.email || '',
        bio: instrutor.bio || '',
        especialidades: instrutor.especialidades || '',
        foto: instrutor.foto || ''
      });
    } else {
      setFormData({
        nome: '',
        email: '',
        bio: '',
        especialidades: '',
        foto: ''
      });
    }
    
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setSelectedInstrutor(null);
    setFormData({
      nome: '',
      email: '',
      bio: '',
      especialidades: '',
      foto: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    setSubmitting(true);
    
    try {
      if (modalMode === 'create') {
        await api.post('/instrutores', formData);
        alert('Instrutor criado com sucesso!');
      } else {
        await api.put(`/instrutores/${selectedInstrutor.id}`, formData);
        alert('Instrutor atualizado com sucesso!');
      }
      
      fecharModal();
      carregarInstrutores();
    } catch (error) {
      console.error('Erro ao salvar instrutor:', error);
      alert('Erro ao salvar instrutor');
    } finally {
      setSubmitting(false);
    }
  };

  const deletarInstrutor = async (instrutor) => {
    if (window.confirm(`Tem certeza que deseja deletar o instrutor "${instrutor.nome}"?`)) {
      try {
        await api.delete(`/instrutores/${instrutor.id}`);
        alert('Instrutor deletado com sucesso!');
        carregarInstrutores();
      } catch (error) {
        console.error('Erro ao deletar instrutor:', error);
        alert('Erro ao deletar instrutor');
      }
    }
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('foto', file);

    try {
      const response = await api.post('/upload/instrutor-foto', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        foto: response.data.arquivo
      }));
      
      alert('Foto enviada com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar foto:', error);
      alert('Erro ao enviar foto');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto"></div>
            <p className="text-white mt-4">Carregando instrutores...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Gerenciar Instrutores</h1>
            <p className="text-slate-400">Gerencie os instrutores da plataforma</p>
          </div>
          <Button
            onClick={() => abrirModal('create')}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Instrutor
          </Button>
        </div>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar instrutores por nome, email ou especialidades..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total de Instrutores</p>
                  <p className="text-3xl font-bold text-white">{instrutores.length}</p>
                </div>
                <User className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-600/20 to-green-800/20 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Com Foto</p>
                  <p className="text-3xl font-bold text-white">
                    {instrutores.filter(i => i.foto).length}
                  </p>
                </div>
                <Upload className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-600/20 to-purple-800/20 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Resultados da Busca</p>
                  <p className="text-3xl font-bold text-white">{filteredInstrutores.length}</p>
                </div>
                <Search className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredInstrutores.map((instrutor) => (
            <Card key={instrutor.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {instrutor.foto ? (
                      <img
                        src={`${API_BASE_URL}/uploads/${instrutor.foto}`}
                        alt={instrutor.nome}
                        className="w-12 h-12 rounded-full object-cover border-2 border-slate-600"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-white">{instrutor.nome}</h3>
                      {instrutor.email && (
                        <p className="text-sm text-slate-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {instrutor.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {instrutor.especialidades && (
                  <div className="mb-4">
                    <Badge className="bg-blue-600/20 text-blue-400 border border-blue-600/50">
                      {instrutor.especialidades}
                    </Badge>
                  </div>
                )}

                {instrutor.bio && (
                  <p className="text-sm text-slate-300 mb-4 line-clamp-3">
                    {instrutor.bio}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => abrirModal('edit', instrutor)}
                    className="flex-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/50"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => deletarInstrutor(instrutor)}
                    className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredInstrutores.length === 0 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <User className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchTerm ? 'Nenhum instrutor encontrado' : 'Nenhum instrutor cadastrado'}
              </h3>
              <p className="text-slate-400 mb-6">
                {searchTerm 
                  ? 'Tente buscar com outros termos'
                  : 'Comece criando seu primeiro instrutor'
                }
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => abrirModal('create')}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Instrutor
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">
                  {modalMode === 'create' ? 'Novo Instrutor' : 'Editar Instrutor'}
                </h2>
                <Button
                  size="sm"
                  onClick={fecharModal}
                  className="bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
                >
                  ✕
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nome completo do instrutor"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Especialidades
                  </label>
                  <input
                    type="text"
                    value={formData.especialidades}
                    onChange={(e) => setFormData(prev => ({ ...prev, especialidades: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: JavaScript, React, Node.js"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Biografia
                  </label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="4"
                    placeholder="Conte um pouco sobre o instrutor, sua experiência e qualificações..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Foto do Instrutor
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFotoUpload}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {formData.foto && (
                      <div className="flex items-center gap-3">
                        <img
                          src={`${API_BASE_URL}/uploads/${formData.foto}`}
                          alt="Preview"
                          className="w-16 h-16 rounded-full object-cover border-2 border-slate-600"
                        />
                        <div className="text-sm text-slate-300">
                          Foto atual
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  >
                    {submitting ? 'Salvando...' : modalMode === 'create' ? 'Criar Instrutor' : 'Salvar Alterações'}
                  </Button>
                  <Button
                    type="button"
                    onClick={fecharModal}
                    className="bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default GerenciarInstrutores; 